import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import ICategory from "../../entities/ICategory";
import IUser from "../../entities/IUser";
import IShop from "../../entities/IShop";
import { theme } from "../../theme";

const customerLabel = (u?: IUser) => {
  if (!u) return "";
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.username || u.emailId || `Customer #${u.userId}`;
};

type Props = {
  categoryData: Array<ICategory>;
  customers?: Array<IUser>;
  loadingCustomers?: boolean;
  shops?: Array<IShop>;
  loadingShops?: boolean;
  customerId?: number;
  shopId?: number;
  onCustomerChange: (customerId: number) => void;
  onShopChange: (shopId: number) => void;
  onNext: (categoryId: number, categoryName: string) => void;
};

// First step of the "create product" flow: pick the user, then the shop

const CategoryPickerStep = ({
  categoryData,
  customers = [],
  loadingCustomers = false,
  shops = [],
  loadingShops = false,
  customerId = 0,
  shopId = 0,
  onCustomerChange,
  onShopChange,
  onNext,
}: Props) => {
  const [categoryId, setCategoryId] = useState<number>(0);

  const selectedCategory = categoryData.find((c: any) => c.categoryId === categoryId);
  const canProceed = Boolean(customerId && shopId && categoryId);

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "12px",
        boxShadow:
          "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
        p: { xs: 2.5, sm: 4 },
        maxWidth: 520,
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: 1 }}
      >
        Get Started
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
        Who's this product for?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Choose the user, their shop, and a category to unlock the right attributes.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Select User
      </Typography>
      <FormControl fullWidth sx={{ mb: 2.5 }}>
        <Select
          displayEmpty
          disabled={loadingCustomers}
          value={customerId || ""}
          onChange={(e) => onCustomerChange(Number(e.target.value))}
          renderValue={(value) =>
            value ? (
              customerLabel(customers.find((c) => c.userId === Number(value))) ||
              `Customer #${value}`
            ) : (
              <Typography color="text.secondary">
                {loadingCustomers ? "Loading users…" : "Choose a user"}
              </Typography>
            )
          }
          sx={{ borderRadius: "10px" }}
        >
          {customers.map((c) => (
            <MenuItem key={c.userId} value={c.userId}>
              {customerLabel(c)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Select Shop
      </Typography>
      <FormControl fullWidth sx={{ mb: 2.5 }}>
        <Select
          displayEmpty
          disabled={loadingShops || !customerId}
          value={shopId || ""}
          onChange={(e) => onShopChange(Number(e.target.value))}
          renderValue={(value) =>
            value ? (
              shops.find((s) => s.shopId === Number(value))?.shopName || `Shop #${value}`
            ) : (
              <Typography color="text.secondary">
                {!customerId
                  ? "Select a user first"
                  : loadingShops
                  ? "Loading shops…"
                  : shops.length === 0
                  ? "This user has no shops"
                  : "Choose a shop"}
              </Typography>
            )
          }
          sx={{ borderRadius: "10px" }}
        >
          {shops.map((s) => (
            <MenuItem key={s.shopId} value={s.shopId}>
              {s.shopName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Select Category
      </Typography>
      <FormControl fullWidth sx={{ mb: 4 }}>
        <Select
          displayEmpty
          value={categoryId || ""}
          disabled={!shopId}
          onChange={(e) => setCategoryId(Number(e.target.value))}
          renderValue={(value) => {
            if (!value || !selectedCategory) {
              return (
                <Typography color="text.secondary">
                  {!shopId ? "Select a shop first" : "Browse all categories"}
                </Typography>
              );
            }
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <CategoryRoundedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                <Typography fontWeight={600}>{selectedCategory.title}</Typography>
              </Box>
            );
          }}
          sx={{ borderRadius: "10px" }}
        >
          {categoryData.map((cat: any) => (
            <MenuItem key={cat.categoryId} value={cat.categoryId}>
              {cat.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        fullWidth
        variant="contained"
        disabled={!canProceed}
        onClick={() => selectedCategory && onNext(selectedCategory.categoryId, selectedCategory.title || "")}
        sx={{ py: 1.4, borderRadius: "10px", fontWeight: 600 }}
      >
        Next Step
      </Button>
    </Box>
  );
};

export default CategoryPickerStep;
